/**
 * PitchInvestors — player fundraising with Jump Gate gate and compliance-based odds.
 */
import type { IAppLogger } from '../../domain/ports/IAppLogger';
import {
  PITCH_BASE_FUNDING,
  PITCH_COMPLIANCE_PENALTY,
  PITCH_COOLDOWN_DAYS,
  PITCH_MAX_BONUS,
  type PitchInvestorsInput,
  type PitchInvestorsOutput,
} from '../dto/EconomyDto';

export interface PitchInvestorsDependencies {
  logger?: IAppLogger;
  random?: () => number;
}

export class PitchInvestorsUseCase {
  constructor(private readonly deps: PitchInvestorsDependencies = {}) {}

  execute(input: PitchInvestorsInput): PitchInvestorsOutput {
    const { stats, hasJumpGate } = input;
    const cooldownDays = input.pitchCooldownDays ?? PITCH_COOLDOWN_DAYS;

    if (!hasJumpGate) {
      return {
        ok: false,
        stats,
        error: 'Pitch requires Admin Jump Gate',
        newsMessage: 'ACCESS DENIED: Pitch requires Admin Jump Gate.',
        newsType: 'negative',
        sfx: 'error',
      };
    }

    if (stats.day - stats.lastPitchDay < cooldownDays) {
      return {
        ok: false,
        stats,
        error: 'Pitch cooldown active',
        newsMessage: 'Investors are still reviewing previous reports. Wait.',
        newsType: 'neutral',
        playErrorSfx: true,
      };
    }

    const complianceFactor = stats.compliance / 100;
    const successChance = 0.4 + complianceFactor * 0.6;
    const roll = input.randomRoll ?? (this.deps.random ?? Math.random)();

    if (roll < successChance) {
      const performanceBonus = Math.floor(complianceFactor * PITCH_MAX_BONUS);
      const totalFunding = PITCH_BASE_FUNDING + performanceBonus;
      const updatedStats = {
        ...stats,
        budget: stats.budget + totalFunding,
        lastPitchDay: stats.day,
      };

      this.deps.logger?.log('debug', 'pitch.success', { totalFunding, roll });

      return {
        ok: true,
        stats: updatedStats,
        fundingAmount: totalFunding,
        newsMessage: `Funding Secured: +$${totalFunding}`,
        newsType: 'positive',
        sfx: 'money',
      };
    }

    const updatedStats = {
      ...stats,
      lastPitchDay: stats.day,
      compliance: Math.max(0, stats.compliance - PITCH_COMPLIANCE_PENALTY),
    };

    this.deps.logger?.log('debug', 'pitch.rejected', { roll, successChance });

    return {
      ok: false,
      stats: updatedStats,
      newsMessage: 'Investor Pitch Rejected. Scandal! Compliance -10%.',
      newsType: 'negative',
      sfx: 'alert',
    };
  }
}